import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { HelpTextPreview } from "./HelpTextPreview";

// Define the help text type
interface FieldHelpText {
  id: string;
  field_id: string;
  custom_text: string | null;
  default_text: string;
  created_at: string;
  updated_at: string;
}

interface HelpTextEditorProps {
  fieldId: string;
  field: any; // Using any for now as we get this from the parent component
  onClose: () => void;
}

export const HelpTextEditor = ({ fieldId, field, onClose }: HelpTextEditorProps) => {
  const [customText, setCustomText] = useState("");
  const [defaultText, setDefaultText] = useState("");
  const [previewFieldName, setPreviewFieldName] = useState(field?.name || "Field");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch existing help text for this field
  const { data: helpTextData, isLoading: isLoadingHelpText } = useQuery({
    queryKey: ["fieldHelpText", fieldId],
    queryFn: async () => {
      const { data, error } = await supabase.from("field_help_texts").select("*").eq("field_id", fieldId).maybeSingle();

      if (error) throw error;
      return data as FieldHelpText | null;
    },
  });

  // Generate default help text based on field type
  useEffect(() => {
    if (field) {
      const generatedDefault = generateDefaultHelpText(field.name, field.type);
      setDefaultText(generatedDefault);

      // If we have custom text, use it, otherwise use the default
      if (helpTextData?.custom_text) {
        setCustomText(helpTextData.custom_text);
      } else {
        setCustomText(generatedDefault);
      }
    }
  }, [field, helpTextData]);

  // Save or update help text
  const mutation = useMutation({
    mutationFn: async () => {
      const helpTextExists = helpTextData?.id;

      if (helpTextExists) {
        // Update existing record
        const { error } = await supabase
          .from("field_help_texts")
          .update({
            custom_text: customText,
            default_text: defaultText,
            updated_at: new Date().toISOString(),
          })
          .eq("id", helpTextData.id);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase.from("field_help_texts").insert({
          field_id: fieldId,
          custom_text: customText,
          default_text: defaultText,
        });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Help text saved successfully");
      queryClient.invalidateQueries({ queryKey: ["fields"] });
      queryClient.invalidateQueries({ queryKey: ["fieldHelpText", fieldId] });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to save help text: ${(error as Error).message}`);
    },
  });

  const resetToDefault = () => {
    setCustomText(defaultText);
    setResetDialogOpen(false);
    toast("Reset to default text");
  };

  const handleSave = () => {
    mutation.mutate();
  };

  const generateDefaultHelpText = (fieldName: string, fieldType: string) => {
    if (!fieldName) return "";

    if (fieldName.toLowerCase().includes("location")) {
      return `To avoid this prompt next time, try clearly specifying where procedures were performed. Example: "Performed snare resection at the ${fieldName}"`;
    }

    if (fieldType === "multi_select") {
      return `To avoid this prompt next time, clearly list all ${fieldName.toLowerCase()} in your text. Example: "Patient has ${fieldName.toLowerCase()} including [options]"`;
    }

    return `To avoid this prompt next time, clearly specify the ${fieldName.toLowerCase()} in your text. Example: "The ${fieldName.toLowerCase()} is [option]"`;
  };

  return (
    <>
      <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Help Text for {field?.name}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="edit" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="edit">Edit</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>

            <TabsContent value="edit" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="customText">Custom Help Text</Label>
                  <Textarea
                    id="customText"
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    className="h-32"
                    placeholder="Enter custom help text here..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use variables like {"{fieldName}"} or {"{isMultiSelect}"} that will be replaced with actual values.
                  </p>
                </div>

                <div>
                  <Label htmlFor="previewFieldName">Preview Field Name (for testing)</Label>
                  <Textarea
                    id="previewFieldName"
                    value={previewFieldName}
                    onChange={(e) => setPreviewFieldName(e.target.value)}
                    className="h-10"
                  />
                </div>

                <div>
                  <Label htmlFor="defaultText">Default Text (read-only)</Label>
                  <Textarea id="defaultText" value={defaultText} readOnly className="h-24 bg-muted" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="preview">
              <div className="border rounded-md p-4">
                <HelpTextPreview
                  fieldName={previewFieldName}
                  helpText={customText}
                  isMultiSelect={field?.type === "multi_select"}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex justify-between">
            <div>
              <Button variant="outline" onClick={() => setResetDialogOpen(true)}>
                Reset to Default
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={mutation.isPending}>
                {mutation.isPending ? "Saving..." : "Save Help Text"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Default?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revert the help text to the system-generated default. Any customizations will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={resetToDefault}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
